import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowRightLeft, Gift, Minus, MinusCircle, Plus, PlusCircle, ReceiptText, ShieldCheck } from "lucide-react";
import { Button, Checkbox, Dialog } from "@akuna/ui";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { OptionChip, OptionTile } from "@/components/packages/plan-card/OptionTile";
import { useStayDailyRates } from "@/hooks/useStayDailyRates";
import {
  STAY_PLAN_ADJUSTMENT_REASONS,
  useApplyStayPlanAdjustment,
  useApplyWeeklyPlanRemoval,
  useReverseWeeklyPlanRemoval,
  useWeeklyPlanRemovalPreview,
  type StayPlanAdjustment,
  type StayPlanAdjustmentReason,
} from "@/hooks/useStayPlanAdjustments";
import { buildStayPlanStatus } from "@/hooks/useStayPlanStatus";
import type { StayPlanContract } from "@/hooks/useStayPlanContracts";
import { formatDateBR, getBrazilDate } from "@/lib/date-utils";

export interface StayPlanBalanceAdjustmentDialogProps {
  contract: StayPlanContract;
  petName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Preenchido quando este ajuste reverte outro; o dialog inverte o delta. */
  reverses?: StayPlanAdjustment | null;
  /** Direção inicial, para o card abrir já em "dar" ou "remover". */
  defaultDirection?: "give" | "remove";
}

function formatCurrency(value: number, language: string) {
  return new Intl.NumberFormat(language.startsWith("pt") ? "pt-BR" : "en-US", {
    style: "currency",
    currency: "BRL",
  }).format(value);
}

export function StayPlanBalanceAdjustmentDialog({
  contract,
  petName,
  open,
  onOpenChange,
  reverses = null,
  defaultDirection = "give",
}: StayPlanBalanceAdjustmentDialogProps) {
  const { t, i18n } = useTranslation("patients");
  const apply = useApplyStayPlanAdjustment();
  const applyWeeklyRemoval = useApplyWeeklyPlanRemoval();
  const reverseWeeklyRemoval = useReverseWeeklyPlanRemoval();
  const rates = useStayDailyRates();

  // Reverter é o mesmo ajuste com o sinal invertido; a direção e a quantidade
  // já nascem espelhando o original.
  const [direction, setDirection] = useState<"give" | "remove">(
    reverses ? (reverses.delta > 0 ? "remove" : "give") : defaultDirection,
  );
  const [quantity, setQuantity] = useState(reverses ? Math.abs(reverses.delta) : 1);
  const [reason, setReason] = useState<StayPlanAdjustmentReason | null>(reverses ? "miscount" : null);
  const [note, setNote] = useState("");
  const [charge, setCharge] = useState<"none" | "extra">("none");
  // Só aparece ao reverter um ajuste que gerou cobrança.
  const [removeCharge, setRemoveCharge] = useState(true);
  const [showErrors, setShowErrors] = useState(false);
  const [cancelStayReservationDayId, setCancelStayReservationDayId] = useState<string | null>(null);

  const today = getBrazilDate();
  const status = useMemo(() => buildStayPlanStatus(contract, today), [contract, today]);
  const isWeeklyRemoval = contract.kind === "recurring" && contract.usageRule === "per_week" && direction === "remove" && !reverses;
  const isWeeklyRemovalReversal = Boolean(reverses && reverses.delta < 0 && reverses.targetWeekStartsOn);
  const weeklyPreview = useWeeklyPlanRemovalPreview({
    module: contract.module,
    contractId: contract.id,
    referenceDate: today,
    enabled: open && isWeeklyRemoval,
  });

  const extraRate =
    contract.module === "daycare" ? rates.data?.daycareDailyRate ?? null : rates.data?.hotelNightlyRate ?? null;
  const canCharge = extraRate !== null && extraRate > 0;

  const delta = (direction === "remove" ? -1 : 1) * (isWeeklyRemoval ? 1 : Math.max(1, quantity));
  const currentBalance = status.cycle ? status.balance.effective ?? 0 : contract.remainingDays ?? 0;
  const nextBalance = Math.max(0, currentBalance + delta);
  const cycleTotal = (status.quota ?? 0) + contract.carryoverUnits + status.adjustmentUnits + delta;

  const noteMissing = reason === "other" && note.trim().length === 0;
  const cancellationRequired = weeklyPreview.data?.kind === "needs_cancellation";
  const canSubmit = reason !== null
    && !noteMissing
    && !apply.isPending
    && !applyWeeklyRemoval.isPending
    && !reverseWeeklyRemoval.isPending
    && (!cancellationRequired || cancelStayReservationDayId !== null);
  const submitDisabled = apply.isPending || applyWeeklyRemoval.isPending || reverseWeeklyRemoval.isPending || (cancellationRequired && cancelStayReservationDayId === null);

  const submit = async () => {
    setShowErrors(true);
    if (!canSubmit || reason === null) return;
    if (isWeeklyRemovalReversal && reverses) {
      await reverseWeeklyRemoval.mutateAsync({
        module: contract.module,
        contractId: contract.id,
        reversesAdjustmentId: reverses.id,
        reason,
        note: note.trim() || null,
      });
    } else if (isWeeklyRemoval) {
      await applyWeeklyRemoval.mutateAsync({
        module: contract.module,
        contractId: contract.id,
        reason,
        note: note.trim() || null,
        cancelStayReservationDayId,
      });
    } else {
      await apply.mutateAsync({
        module: contract.module,
        contractId: contract.id,
        delta,
        reason,
        note: note.trim() || null,
        chargeAmount: charge === "extra" && canCharge ? extraRate : null,
        reversesId: reverses?.id ?? null,
        removeCharge: Boolean(reverses?.chargeSaleItemId) && removeCharge,
      });
    }
    onOpenChange(false);
  };

  const subtitle = status.cycle
    ? t("stayPlanAdjustments.subtitle", {
        plan: contract.planName,
        pet: petName,
        start: formatDateBR(status.cycle.start),
        end: formatDateBR(status.cycle.end),
      })
    : t("stayPlanAdjustments.subtitleNoCycle", { plan: contract.planName, pet: petName });

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      mode="adaptive"
      className="sm:max-w-[620px]"
      title={t("stayPlanAdjustments.title")}
      header={subtitle}
      closeLabel={t("stayPlanAdjustments.cancel")}
      footer={
        <div className="flex w-full items-center gap-3">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            {t("stayPlanAdjustments.cancel")}
          </Button>
          <Button type="button" className="ms-auto" disabled={submitDisabled} onClick={() => void submit()}>
            {t("stayPlanAdjustments.submit")}
          </Button>
        </div>
      }
    >
      <div className="grid gap-[18px]">
        <fieldset>
          <legend className="mb-2 text-[0.8125rem] font-semibold text-foreground">
            {t("stayPlanAdjustments.actionLabel")}
          </legend>
          <div className="grid gap-2.5 sm:grid-cols-2">
            <OptionTile
              name="adjustment-direction"
              value="give"
              checked={direction === "give"}
              onSelect={() => setDirection("give")}
            >
              <span className="flex items-center gap-2.5">
                <PlusCircle className="h-[18px] w-[18px] shrink-0 text-primary" aria-hidden />
                <span className="text-sm font-semibold text-foreground">{t("stayPlanAdjustments.give")}</span>
              </span>
            </OptionTile>
            <OptionTile
              name="adjustment-direction"
              value="remove"
              checked={direction === "remove"}
              onSelect={() => setDirection("remove")}
            >
              <span className="flex items-center gap-2.5">
                <MinusCircle className="h-[18px] w-[18px] shrink-0 text-muted-foreground" aria-hidden />
                <span className="text-sm font-semibold text-color-medium">{t("stayPlanAdjustments.remove")}</span>
              </span>
            </OptionTile>
          </div>
        </fieldset>

        <div className="grid items-end gap-3.5 sm:grid-cols-[180px_minmax(0,1fr)]">
          <div>
            <p id="adjustment-quantity-label" className="mb-2 text-[0.8125rem] font-semibold text-foreground">
              {t("stayPlanAdjustments.quantity")}
            </p>
            <div
              role="group"
              aria-labelledby="adjustment-quantity-label"
              className="flex items-center justify-between rounded-md border-[1.5px] border-border p-1.5"
            >
              <button
                type="button"
                aria-label={t("stayPlanAdjustments.decrease")}
                disabled={isWeeklyRemoval || quantity <= 1}
                onClick={() => setQuantity((value) => Math.max(1, value - 1))}
                className="flex h-8 w-8 items-center justify-center rounded-sm border border-border bg-background text-color-medium transition-colors hover:bg-foreground/[0.05] disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <Minus className="h-4 w-4" aria-hidden />
              </button>
              <span className="font-display text-lg font-extrabold text-foreground">{isWeeklyRemoval ? 1 : quantity}</span>
              <button
                type="button"
                aria-label={t("stayPlanAdjustments.increase")}
                disabled={isWeeklyRemoval}
                onClick={() => setQuantity((value) => value + 1)}
                className="flex h-8 w-8 items-center justify-center rounded-sm border border-border bg-background text-color-medium transition-colors hover:bg-foreground/[0.05] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <Plus className="h-4 w-4" aria-hidden />
              </button>
            </div>
          </div>

          <p
            role="status"
            aria-live="polite"
            className="flex items-center gap-3 rounded-md border border-border bg-cream-50 px-4 py-3 text-[0.8125rem] text-color-medium"
          >
            <ArrowRightLeft className="h-[18px] w-[18px] shrink-0 text-primary" aria-hidden />
            {isWeeklyRemoval && weeklyPreview.data?.kind === "target"
              ? t("stayPlanAdjustments.weeklyRemovalTarget", { date: formatDateBR(weeklyPreview.data.targetWeekStartsOn) })
              : isWeeklyRemoval && weeklyPreview.data?.kind === "needs_cancellation"
                ? t("stayPlanAdjustments.futureReservationsFull")
                : status.cycle
              ? t("stayPlanAdjustments.preview", { from: currentBalance, to: nextBalance, total: cycleTotal })
              : t("stayPlanAdjustments.previewNoCycle", { from: currentBalance, to: nextBalance })}
          </p>
        </div>

        {isWeeklyRemoval && weeklyPreview.data?.kind === "needs_cancellation" ? (
          <fieldset>
            <legend className="mb-2 text-[0.8125rem] font-semibold text-foreground">
              {t("stayPlanAdjustments.chooseCancellation")}
            </legend>
            <div className="grid gap-2">
              {weeklyPreview.data.candidates.map((candidate) => (
                <OptionTile
                  key={candidate.stayReservationDayId}
                  name="weekly-removal-candidate"
                  value={candidate.stayReservationDayId}
                  checked={cancelStayReservationDayId === candidate.stayReservationDayId}
                  onSelect={() => setCancelStayReservationDayId(candidate.stayReservationDayId)}
                >
                  <span className="text-sm font-semibold text-foreground">{formatDateBR(candidate.stayDate)}</span>
                </OptionTile>
              ))}
            </div>
          </fieldset>
        ) : null}

        <fieldset>
          <legend className="mb-2 flex flex-wrap items-center gap-2">
            <span className="text-[0.8125rem] font-semibold text-foreground">{t("stayPlanAdjustments.reason")}</span>
            <span className="rounded-full bg-state-danger-soft px-2 py-0.5 text-[0.7rem] font-semibold text-state-danger">
              {t("stayPlanAdjustments.reasonRequired")}
            </span>
          </legend>
          <div className="flex flex-wrap gap-2">
            {STAY_PLAN_ADJUSTMENT_REASONS.map((option) => (
              <OptionChip
                key={option}
                name="adjustment-reason"
                value={option}
                checked={reason === option}
                onSelect={() => setReason(option)}
              >
                {t(`stayPlanAdjustments.reasons.${option}`)}
              </OptionChip>
            ))}
          </div>

          <div className="mt-2.5">
            <Label htmlFor="adjustment-note" className="sr-only">
              {t("stayPlanAdjustments.note")}
            </Label>
            <Textarea
              id="adjustment-note"
              value={note}
              placeholder={t("stayPlanAdjustments.notePlaceholder")}
              onChange={(event) => setNote(event.target.value)}
              aria-describedby="adjustment-note-hint"
              aria-invalid={showErrors && noteMissing}
              className="min-h-[66px]"
            />
            <p id="adjustment-note-hint" className="mt-1.5 text-xs text-color-light">
              {t("stayPlanAdjustments.noteHint")}
            </p>
            {showErrors && noteMissing ? (
              <p className="mt-1 text-xs text-destructive">{t("stayPlanAdjustments.noteRequired")}</p>
            ) : null}
          </div>
        </fieldset>

        <fieldset>
          <legend className="mb-2 text-[0.8125rem] font-semibold text-foreground">
            {t("stayPlanAdjustments.chargeTitle")}
          </legend>
          <div className="grid gap-2.5 sm:grid-cols-2">
            <OptionTile
              name="adjustment-charge"
              value="none"
              checked={charge === "none"}
              onSelect={() => setCharge("none")}
            >
              <span className="flex items-center gap-2">
                <Gift className="h-4 w-4 shrink-0 text-primary" aria-hidden />
                <span className="text-sm font-semibold text-foreground">{t("stayPlanAdjustments.chargeNone")}</span>
              </span>
              <span className="mt-1.5 block text-xs text-muted-foreground">
                {t("stayPlanAdjustments.chargeNoneHint")}
              </span>
            </OptionTile>
            <OptionTile
              name="adjustment-charge"
              value="extra"
              checked={charge === "extra"}
              disabled={!canCharge}
              onSelect={() => setCharge("extra")}
            >
              <span className="flex items-center gap-2">
                <ReceiptText className="h-4 w-4 shrink-0 text-primary" aria-hidden />
                <span className="text-sm font-semibold text-foreground">{t("stayPlanAdjustments.chargeExtra")}</span>
              </span>
              <span className="mt-1.5 block text-xs text-muted-foreground">
                {canCharge
                  ? t("stayPlanAdjustments.chargeExtraHint", {
                      amount: formatCurrency((extraRate ?? 0) * Math.max(1, quantity), i18n.language),
                    })
                  : t("stayPlanAdjustments.chargeUnavailable")}
              </span>
            </OptionTile>
          </div>
        </fieldset>

        {reverses?.chargeSaleItemId ? (
          <label className="flex items-start gap-2.5 rounded-md border border-border p-3.5 text-sm">
            <Checkbox checked={removeCharge} onCheckedChange={(value) => setRemoveCharge(value === true)} />
            <span>
              {t("stayPlanAdjustments.removeCharge")}
              <span className="block text-xs text-muted-foreground">
                {t("stayPlanAdjustments.removeChargeHint")}
              </span>
            </span>
          </label>
        ) : null}

        <p className="flex items-center gap-2.5 rounded-md border border-border bg-cream-50 px-3.5 py-3 text-xs text-color-medium">
          <ShieldCheck className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
          {t("stayPlanAdjustments.immutableHint")}
        </p>
      </div>
    </Dialog>
  );
}
